import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import {
  useCreateApiKeyMutation,
  type CreateApiKeyResponse,
} from '@hooks/requests/queries/use_create_api_key_mutation';
import { useToast } from '@hooks/useToast';
import { describeMutationError } from '@hooks/requests/utils/_partner_api_client';
import { Modal, Button, Input, Alert } from '../ui';

const NAME_MAX_LENGTH = 30;

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Create API key modal (sc-45103, AC9-AC13).
 *
 * Two phases in one modal: naming the key (AC10), then showing its API key
 * exactly once (AC11) before returning to the list, where it appears masked
 * (AC12). The 2-key cap (AC9) is checked by the caller (`ApiKeysPage`)
 * before this modal ever opens, from the key list already in the table — no
 * separate call — but the create call can still answer 409 here on the rare
 * race (AC14's mirror: a second admin created a key in the moment between
 * that check and this submit), so that failure gets its own copy too.
 *
 * The success view (Figma 3188:11884, "Modal - rename key") is a green
 * check-circle + "New API Key" title, a warning banner ("copy it now"),
 * the key's name in quotes, then two independently-copyable fields — the
 * design only shows one value box, but that value is the client_secret
 * alone; "Secret Key" (the client_id, `created.id`) is added here so a
 * partner can grab both halves of the client_credentials pair without
 * leaving this one-time screen — and a single "I've copied it" button
 * that closes the modal, no separate footer/Done step.
 */
const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<CreateApiKeyResponse | null>(null);
  const [copiedField, setCopiedField] = useState<'id' | 'secret' | null>(null);

  const createMutation = useCreateApiKeyMutation();
  const { showToast } = useToast();

  const resetForm = () => {
    setName('');
    setSubmitAttempted(false);
    setError('');
    setCreated(null);
    setCopiedField(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0 && trimmedName.length <= NAME_MAX_LENGTH;

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!isNameValid) return;
    setError('');

    try {
      const result = await createMutation.mutateAsync({ name: trimmedName });
      setCreated(result);
    } catch (err) {
      setError(
        describeMutationError(err, {
          codeMessages: {
            key_limit_reached:
              'This account already has 2 API keys. Delete one before creating another.',
          },
          fallback: 'Failed to create the API key',
        })
      );
    }
  };

  const handleCopy = async (field: 'id' | 'secret') => {
    if (!created) return;
    await navigator.clipboard.writeText(field === 'id' ? created.id : created.apiKey);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleDone = () => {
    showToast(`${created?.name || 'New key'} created`);
    handleClose();
  };

  if (created) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleDone}
        title={
          <span className="api-keys-success-title">
            <FontAwesomeIcon icon={faCircleCheck} className="api-keys-success-check" />
            New API Key
          </span>
        }
        headerVariant="tinted"
        className="api-keys-modal"
        bodyClassName="api-keys-modal__body"
        footerClassName="api-keys-modal__foot"
        closeOnOverlayClick={false}
      >
        <div className="space-y-4">
          <Alert variant="warning">
            Copy this key now. This is the only time we can show the full value.
          </Alert>
          <p className="api-keys-value-label">&ldquo;{created.name}&rdquo;</p>
          <div>
            <p className="api-keys-value-label">Secret Key</p>
            <div className="api-keys-value-box">
              <code className="api-keys-value">{created.id}</code>
              <button
                type="button"
                className="api-keys-value-copy-btn"
                onClick={() => handleCopy('id')}
                aria-label="Copy secret key"
              >
                <FontAwesomeIcon icon={copiedField === 'id' ? faCheck : faCopy} />
              </button>
            </div>
          </div>
          <div>
            <p className="api-keys-value-label">Secret Value</p>
            <div className="api-keys-value-box">
              <code className="api-keys-value">{created.apiKey}</code>
              <button
                type="button"
                className="api-keys-value-copy-btn"
                onClick={() => handleCopy('secret')}
                aria-label="Copy secret value"
              >
                <FontAwesomeIcon icon={copiedField === 'secret' ? faCheck : faCopy} />
              </button>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="secondary" className="au-modal-pill-btn--cancel" onClick={handleDone}>
              I&apos;ve copied it
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create API key"
      headerVariant="tinted"
      className="api-keys-modal"
      bodyClassName="api-keys-modal__body"
      footerClassName="api-keys-modal__foot"
      footer={
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" className="au-modal-pill-btn--cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="au-modal-pill-btn--confirm"
            onClick={handleSubmit}
            isLoading={createMutation.isPending}
          >
            Create key
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          label="Key name"
          placeholder="e.g. Warehouse sync"
          value={name}
          maxLength={NAME_MAX_LENGTH}
          onChange={(event) => setName(event.target.value)}
          error={submitAttempted && !isNameValid ? 'Enter a name up to 30 characters' : undefined}
          hint={
            submitAttempted && !isNameValid ? undefined : `${trimmedName.length}/${NAME_MAX_LENGTH}`
          }
          hintAlign="right"
          autoFocus
        />
        {error && <Alert variant="danger">{error}</Alert>}
      </div>
    </Modal>
  );
};

export default CreateApiKeyModal;
